import { useParams, Link } from 'react-router-dom';
import { shows, breadcrumbMap } from '@/mocks/shows';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const show = shows.find((s) => s.id === id);

  if (!show) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <h1 className="font-heading text-2xl text-foreground-950 mb-3">内容不存在</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm cursor-pointer">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  // Related shows (same category, exclude current)
  const relatedShows = shows
    .filter((s) => s.categorySlug === show.categorySlug && s.id !== show.id)
    .slice(0, 4);

  // Schema
  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: `${show.title} - 屁屁影院免费在线观看`,
        description: show.description,
        datePublished: show.releaseDate,
        author: { '@type': 'Person', name: show.director || '屁屁影院' },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `https://pipicinema.com/article/${show.id}`,
        },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://pipicinema.com' },
          { '@type': 'ListItem', position: 2, name: show.categoryName, item: `https://pipicinema.com/category/${show.categorySlug}` },
          { '@type': 'ListItem', position: 3, name: show.title, item: `https://pipicinema.com/article/${show.id}` },
        ],
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />

      <div className="pt-20 md:pt-24 pb-10">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: show.categoryName, href: `/category/${show.categorySlug}` },
              { label: show.title },
            ]}
          />

          {/* Detail Content */}
          <article className="flex flex-col lg:flex-row gap-8 mt-4">
            {/* Poster */}
            <div className="w-full lg:w-[360px] flex-shrink-0">
              <div className="relative rounded-xl overflow-hidden aspect-[3/4]">
                <img
                  src={show.image}
                  alt={`${show.title} - 屁屁影院免费在线观看`}
                  title={`${show.title} - 屁屁影院${show.categoryName}免费在线观看`}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-transparent"></div>
                {/* Play Button */}
                <div className="absolute bottom-4 left-4 right-4">
                  <button className="w-full flex items-center justify-center gap-2 py-3 bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-all text-sm font-medium cursor-pointer whitespace-nowrap">
                    <i className="ri-play-fill text-lg w-5 h-5 flex items-center justify-center"></i>
                    立即播放
                  </button>
                </div>
              </div>
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <h1 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-3">
                {show.title}
              </h1>

              {/* Meta */}
              <div className="flex items-center flex-wrap gap-3 mb-4">
                <span className="flex items-center gap-1 text-accent-500 text-sm font-medium">
                  <i className="ri-star-fill w-4 h-4 flex items-center justify-center"></i>
                  {show.rating}
                </span>
                <span className="text-sm text-foreground-500">{show.year}</span>
                <span className="text-sm text-foreground-500">{show.region}</span>
                <span className="text-sm text-foreground-500">{show.duration}</span>
                <span className="text-sm text-foreground-500">{show.episodes}</span>
              </div>

              {/* Tags */}
              <div className="flex items-center flex-wrap gap-2 mb-5">
                {show.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              {/* Description */}
              <div className="mb-5">
                <h2 className="font-heading text-base text-foreground-950 mb-2">剧情简介</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {show.longDescription}
                </p>
              </div>

              {/* Details Table */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 mb-5 text-sm">
                {show.director && (
                  <div className="flex gap-2">
                    <span className="text-foreground-400 flex-shrink-0 whitespace-nowrap">导演：</span>
                    <span className="text-foreground-700">{show.director}</span>
                  </div>
                )}
                {show.actors.length > 0 && (
                  <div className="flex gap-2">
                    <span className="text-foreground-400 flex-shrink-0 whitespace-nowrap">主演：</span>
                    <span className="text-foreground-700">{show.actors.join(' / ')}</span>
                  </div>
                )}
                <div className="flex gap-2">
                  <span className="text-foreground-400 flex-shrink-0 whitespace-nowrap">地区：</span>
                  <span className="text-foreground-700">{show.region}</span>
                </div>
                <div className="flex gap-2">
                  <span className="text-foreground-400 flex-shrink-0 whitespace-nowrap">语言：</span>
                  <span className="text-foreground-700">{show.language}</span>
                </div>
                <div className="flex gap-2">
                  <span className="text-foreground-400 flex-shrink-0 whitespace-nowrap">类型：</span>
                  <span className="text-foreground-700">{show.subcategory}</span>
                </div>
                <div className="flex gap-2">
                  <span className="text-foreground-400 flex-shrink-0 whitespace-nowrap">更新：</span>
                  <span className="text-foreground-700">{show.updateDay}</span>
                </div>
              </div>
            </div>
          </article>

          {/* Related */}
          {relatedShows.length > 0 && (
            <section className="mt-12">
              <h2 className="font-heading text-xl text-foreground-950 mb-5">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {relatedShows.map((s) => (
                  <Link
                    key={s.id}
                    to={`/article/${s.id}`}
                    className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative w-full aspect-[4/3] overflow-hidden">
                      <img
                        src={s.image}
                        alt={s.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-foreground-950/0 group-hover:bg-foreground-950/30 transition-colors duration-300 flex items-center justify-center">
                        <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500/90 text-background-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <i className="ri-play-fill text-lg"></i>
                        </span>
                      </div>
                      <span className="absolute top-2 right-2 px-1.5 py-0.5 text-xs bg-foreground-950/70 text-accent-500 rounded-md flex items-center gap-0.5 whitespace-nowrap">
                        <i className="ri-star-fill text-[10px] w-3 h-3 flex items-center justify-center"></i>
                        {s.rating}
                      </span>
                    </div>
                    <div className="p-3">
                      <h3 className="text-sm font-medium text-foreground-950 line-clamp-1 group-hover:text-primary-600 transition-colors">
                        {s.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1">{s.subcategory} · {s.year}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </>
  );
}